import { PROJECT_VALIDATION_LIMITS, CLEARANCE_TYPE_OPTIONS } from '../constants/projectConstants';
import { financialYearUtils } from './helpers';

// Validate required field
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// Validate project name
export const validateProjectName = (projectName) => {
  if (!projectName || !projectName.trim()) {
    return 'Project name is required';
  }
  
  const trimmedName = projectName.trim();
  
  if (trimmedName.length < PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH) {
    return `Project name must be at least ${PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MIN_LENGTH} characters`;
  }
  
  if (trimmedName.length > PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH) {
    return `Project name cannot exceed ${PROJECT_VALIDATION_LIMITS.PROJECT_NAME_MAX_LENGTH} characters`;
  }
  
  return null;
};

// UPDATED: Validate financial requirement - IN LAKHS
export const validateFinancialRequirement = (amount) => {
  if (!amount || amount.toString().trim() === '') {
    return 'Financial requirement is required';
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return 'Please enter a valid amount';
  }
  
  if (numAmount < PROJECT_VALIDATION_LIMITS.FINANCIAL_REQUIREMENT_MIN) {
    return 'Amount must be greater than 0';
  }
  
  if (numAmount > PROJECT_VALIDATION_LIMITS.FINANCIAL_REQUIREMENT_MAX) {
    return 'Amount cannot exceed ₹99,999 lakhs';
  }
  
  return null;
};

// UPDATED: Validate approved amount - IN LAKHS - NOW ALWAYS REQUIRED FOR APPROVAL
export const validateApprovedAmount = (amount, isRequired = true) => {
  // Approved amount is now always required when this function is called during approval
  if (!amount || amount.toString().trim() === '') {
    return isRequired ? 'Approved amount is required' : null;
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return 'Please enter a valid approved amount';
  }
  
  if (numAmount < PROJECT_VALIDATION_LIMITS.APPROVED_AMOUNT_MIN) {
    return 'Approved amount must be greater than 0';
  }
  
  if (numAmount > PROJECT_VALIDATION_LIMITS.APPROVED_AMOUNT_MAX) {
    return 'Approved amount cannot exceed ₹99,999 lakhs';
  }
  
  return null;
};

// UPDATED: Validate allocated amount - IN LAKHS
export const validateAllocatedAmount = (amount) => {
  if (!amount || amount.toString().trim() === '') {
    return null; // Optional field
  }
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) {
    return 'Please enter a valid amount';
  }
  
  if (numAmount < PROJECT_VALIDATION_LIMITS.ALLOCATED_AMOUNT_MIN) {
    return 'Amount cannot be negative';
  }
  
  if (numAmount > PROJECT_VALIDATION_LIMITS.ALLOCATED_AMOUNT_MAX) {
    return 'Amount cannot exceed ₹99,999 lakhs';
  }
  
  return null;
};

// NEW: Validate priority
export const validatePriority = (priority) => {
  // Priority is optional
  if (!priority || priority.toString().trim() === '') {
    return null;
  }
  
  const numPriority = parseInt(priority);
  
  if (isNaN(numPriority)) {
    return 'Priority must be a number';
  }
  
  if (numPriority < PROJECT_VALIDATION_LIMITS.PRIORITY_MIN) {
    return `Priority must be at least ${PROJECT_VALIDATION_LIMITS.PRIORITY_MIN}`;
  }
  
  if (numPriority > PROJECT_VALIDATION_LIMITS.PRIORITY_MAX) {
    return `Priority cannot exceed ${PROJECT_VALIDATION_LIMITS.PRIORITY_MAX}`;
  }
  
  // Check if it's a whole number
  if (numPriority !== parseFloat(priority)) {
    return 'Priority must be a whole number';
  }
  
  return null;
};

// Validate UO Number
export const validateUONumber = (uoNumber) => {
  if (!uoNumber || !uoNumber.trim()) {
    return null; // Optional field
  }
  
  const trimmed = uoNumber.trim();
  
  if (trimmed.length < PROJECT_VALIDATION_LIMITS.UO_NUMBER_MIN_LENGTH) {
    return 'UO Number is required';
  }
  
  if (trimmed.length > PROJECT_VALIDATION_LIMITS.UO_NUMBER_MAX_LENGTH) {
    return `UO Number cannot exceed ${PROJECT_VALIDATION_LIMITS.UO_NUMBER_MAX_LENGTH} characters`;
  }
  
  return null;
};

// Validate clearance date
export const validateClearanceDate = (date) => {
  if (!date) {
    return null; // Optional field
  }
  
  return null;
};

// Validate clearance type
export const validateClearanceType = (type) => {
  if (!type) {
    return null; // Optional field
  }
  
  const validTypes = CLEARANCE_TYPE_OPTIONS.map(option => option.value);
  if (!validTypes.includes(type)) {
    return 'Invalid clearance type';
  }
  
  return null;
};

// Validate brief
export const validateBrief = (brief) => {
  if (!brief || !brief.trim()) {
    return 'Brief description is required';
  }
  
  const trimmedBrief = brief.trim();
  
  if (trimmedBrief.length < PROJECT_VALIDATION_LIMITS.BRIEF_MIN_LENGTH) {
    return `Brief must be at least ${PROJECT_VALIDATION_LIMITS.BRIEF_MIN_LENGTH} characters`;
  }
  
  if (trimmedBrief.length > PROJECT_VALIDATION_LIMITS.BRIEF_MAX_LENGTH) {
    return `Brief cannot exceed ${PROJECT_VALIDATION_LIMITS.BRIEF_MAX_LENGTH} characters`;
  }
  
  return null;
};

// Main form validation function (project creation)
export const validateProjectForm = (formData, isAdmin = false) => {
  const errors = {};
  
  // Sector validation - required for admin, auto-filled for department users
  if (isAdmin) {
    const sectorError = validateRequired(formData.sector, 'Sector');
    if (sectorError) errors.sector = sectorError;
  }
  
  // Department validation - required for admin, auto-filled for department users  
  if (isAdmin) {
    const departmentError = validateRequired(formData.department, 'Department');
    if (departmentError) errors.department = departmentError;
  }
  
  // Project name validation
  const projectNameError = validateProjectName(formData.projectName);
  if (projectNameError) errors.projectName = projectNameError;
  
  // Financial requirement validation
  const financialError = validateFinancialRequirement(formData.financialRequirement);
  if (financialError) errors.financialRequirement = financialError;
  
  // Brief validation
  const briefError = validateBrief(formData.brief);
  if (briefError) errors.brief = briefError;
  
  // Priority validation (optional)
  const priorityError = validatePriority(formData.priority);
  if (priorityError) errors.priority = priorityError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Validate clearance data
export const validateClearanceData = (clearanceData) => {
  const errors = {};
  
  const typeError = validateClearanceType(clearanceData.clearanceType);
  if (typeError) errors.clearanceType = typeError;
  
  const amountError = validateAllocatedAmount(clearanceData.allocatedAmount);
  if (amountError) errors.allocatedAmount = amountError;
  
  const uoError = validateUONumber(clearanceData.uoNumber);
  if (uoError) errors.uoNumber = uoError;
  
  const dateError = validateClearanceDate(clearanceData.clearanceDate);
  if (dateError) errors.clearanceDate = dateError;
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// UPDATED: Validate project status change with approved amount - NOW MANDATORY FOR APPROVAL
export const validateStatusChange = (newStatus, approvedAmount, financialRequirement) => {
  const errors = {};
  
  // If changing to approved, approved amount is ALWAYS required
  if (newStatus === 'approved') {
    if (!approvedAmount || approvedAmount.toString().trim() === '') {
      errors.approvedAmount = 'Approved amount is required when approving a project';
    } else {
      const amountError = validateApprovedAmount(approvedAmount, true);
      if (amountError) errors.approvedAmount = amountError;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// UPDATED: Sanitize project data - NO CONVERSION, KEEP IN LAKHS
export const sanitizeProjectData = (projectData) => {
  const sanitized = { ...projectData };
  
  // Trim string fields
  if (sanitized.projectName) sanitized.projectName = sanitized.projectName.trim();
  if (sanitized.brief) sanitized.brief = sanitized.brief.trim();
  if (sanitized.sector) sanitized.sector = sanitized.sector.trim();
  if (sanitized.department) sanitized.department = sanitized.department.trim();
  if (sanitized.uoNumber) sanitized.uoNumber = sanitized.uoNumber.trim();
  if (sanitized.clearanceType) sanitized.clearanceType = sanitized.clearanceType.trim();
  
  // Auto-calculate financial year based on current date
  sanitized.financialYear = financialYearUtils.calculateFinancialYear();
  
  // Clean up financial fields - NO CONVERSION, keep as lakhs
  if (sanitized.financialRequirement) {
    sanitized.financialRequirement = parseFloat(sanitized.financialRequirement.toString().replace(/[^\d.]/g, ''));
  }
  
  if (sanitized.approvedAmount && sanitized.approvedAmount.toString().trim() !== '') {
    sanitized.approvedAmount = parseFloat(sanitized.approvedAmount.toString().replace(/[^\d.]/g, ''));
  } else {
    sanitized.approvedAmount = null;
  }
  
  if (sanitized.allocatedAmount && sanitized.allocatedAmount.toString().trim() !== '') {
    sanitized.allocatedAmount = parseFloat(sanitized.allocatedAmount.toString().replace(/[^\d.]/g, ''));
  } else {
    sanitized.allocatedAmount = null;
  }
  
  // NEW: Clean up priority field
  if (sanitized.priority && sanitized.priority.toString().trim() !== '') {
    sanitized.priority = parseInt(sanitized.priority.toString().replace(/[^\d]/g, ''));
  } else {
    sanitized.priority = null;
  }
  
  // Handle clearance date
  if (sanitized.clearanceDate && sanitized.clearanceDate !== '') {
    sanitized.clearanceDate = new Date(sanitized.clearanceDate);
  } else {
    sanitized.clearanceDate = null;
  }
  
  // Set clearance type to null if empty
  if (!sanitized.clearanceType || sanitized.clearanceType.trim() === '') {
    sanitized.clearanceType = null;
  }
  
  // Set UO number to null if empty
  if (!sanitized.uoNumber || sanitized.uoNumber.trim() === '') {
    sanitized.uoNumber = null;
  }
  
  // Remove undefined values
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === undefined) {
      delete sanitized[key];
    }
  });
  
  return sanitized;
};

// UPDATED: Sanitize clearance data - NO CONVERSION, keep as lakhs
export const sanitizeClearanceData = (clearanceData) => {
  const sanitized = { ...clearanceData };
  
  // Clean string fields
  if (sanitized.clearanceType) {
    sanitized.clearanceType = sanitized.clearanceType.trim();
  }
  
  if (sanitized.uoNumber) {
    sanitized.uoNumber = sanitized.uoNumber.trim();
  }
  
  // Clean amount - NO CONVERSION, keep as lakhs
  if (sanitized.allocatedAmount && sanitized.allocatedAmount.toString().trim() !== '') {
    sanitized.allocatedAmount = parseFloat(sanitized.allocatedAmount.toString().replace(/[^\d.]/g, ''));
  } else {
    sanitized.allocatedAmount = null;
  }
  
  // Handle date
  if (sanitized.clearanceDate && sanitized.clearanceDate !== '') {
    sanitized.clearanceDate = new Date(sanitized.clearanceDate);
  } else {
    sanitized.clearanceDate = null;
  }
  
  // Remove empty strings and convert to null
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] === '' || sanitized[key] === undefined) {
      sanitized[key] = null;
    }
  });
  
  return sanitized;
};

// Validate project data for display/editing
export const validateProjectData = (projectData) => {
  const warnings = [];
  
  // Check if financial year needs recalculation
  if (projectData.createdAt) {
    const calculatedFY = financialYearUtils.calculateFinancialYear(projectData.createdAt);
    if (projectData.financialYear !== calculatedFY) {
      warnings.push(`Financial year mismatch: Expected ${calculatedFY}, found ${projectData.financialYear}`);
    }
  }
  
  // Check if approved amount exists for approved projects
  if (projectData.status === 'approved' && !projectData.approvedAmount) {
    warnings.push('Approved projects should have an approved amount');
  }
  
  return {
    isValid: warnings.length === 0,
    warnings
  };
};

// Default export
export default {
  validateRequired,
  validateProjectName,
  validateFinancialRequirement,
  validateApprovedAmount,
  validateAllocatedAmount,
  validateUONumber,
  validateClearanceDate,
  validateClearanceType,
  validateBrief,
  validatePriority, // NEW
  validateProjectForm,
  validateClearanceData,
  validateStatusChange,
  sanitizeProjectData,
  sanitizeClearanceData,
  validateProjectData
};